import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Glyphicon } from 'react-bootstrap';
import request from 'axios';
import { getClientAuthHeader } from '../../../client/token';
import { getOwnerId, getFileExtension, isDemoAccount } from '../../helpers/utils';
import { getClientProfilePictureFolder } from '../../helpers/fileUtils';
import { contactFormSaveAction } from '../../actions/formActions';
import { clientEditAction } from '../../actions/clientActions';
import { caseClientSetAction, caseSetServerError } from '../../actions/caseActions';
import ProfilePictureEditor from './ProfilePictureEditor';

const PROFILE_PICTURE_URL_FIELD = '$ProfilePictureUrl$';
const PROFILE_PICTURE_KEY_FIELD = '$ProfilePictureKey$';
const PROFILE_PICTURE_CROP_FIELD = '$ProfilePictureCrop$';
const MAX_SIZE_BYTES = 1.5 * 1024 * 1024;
const ACCEPTED_EXTENSIONS = ['jpg', 'jpeg', 'png'];
const ACCEPT_ATTR = 'image/jpeg,image/png';
const FALLBACK_COLOR = '#D9EDF7';

let FineUploaderS3;

class ClientAvatar extends PureComponent {
    constructor(props) {
        super(props);
        this.handleHoverIn = this.handleHoverIn.bind(this);
        this.handleHoverOut = this.handleHoverOut.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onFileSelected = this.onFileSelected.bind(this);
        this.onUploadComplete = this.onUploadComplete.bind(this);
        this.onUploadError = this.onUploadError.bind(this);
        this.onRemove = this.onRemove.bind(this);
        this.openFilePicker = this.openFilePicker.bind(this);
        this.onEditorSave = this.onEditorSave.bind(this);
        this.onEditorCancel = this.onEditorCancel.bind(this);
        this.onEditorError = this.onEditorError.bind(this);
        this.pendingKeyByFileId = {};
        this.state = {
            uploading: false,
            uploadProgress: 0,
            uploaderReady: false,
            hover: false,
            editorOpen: false,
            editorFile: null,
            editorImageUrl: null,
            editorInitScale: undefined,
            editorInitPosition: undefined,
        };
    }

    componentDidMount() {
        this.initUploader();
    }

    componentDidUpdate(prevProps) {
        const prevId = prevProps.client && prevProps.client.id;
        const id = this.props.client && this.props.client.id;
        if (this.props.editable && id && prevId !== id) {
            this.uploader = undefined;
            this.pendingKeyByFileId = {};
            this.setState({ uploaderReady: false }, () => this.initUploader());
        } else if (this.props.editable && id && !this.uploader && !this.state.uploaderReady) {
            this.initUploader();
        }
    }

    async initUploader() {
        if (!__CLIENT__ || !this.props.editable || !this.props.loggedIn) {
            return;
        }

        const client = this.props.client;
        if (!client || !client.id || this.uploader) {
            return;
        }

        if (!FineUploaderS3) {
            FineUploaderS3 = (await import('fine-uploader-wrappers/s3')).default;
        }

        const ownerId = getOwnerId(this.props.loggedIn);
        const folder = getClientProfilePictureFolder(ownerId, client.id);
        const authHeaders = getClientAuthHeader().headers;

        this.uploader = new FineUploaderS3({
            options: {
                request: {
                    endpoint: `${window.location.protocol}//${process.env.AWS_S3_BUCKET}.s3.amazonaws.com`,
                    accessKey: process.env.AWS_CLIENT_PUBLIC_ACCESS_KEY_ID,
                },
                signature: {
                    customHeaders: authHeaders,
                    endpoint: '/api/v1/uploads/signature',
                },
                objectProperties: {
                    // No `acl` — buckets with "Bucket owner enforced" (ACLs
                    // disabled) reject uploads that specify an ACL. Matches the
                    // working case-file/template uploader.
                    key: (id) => {
                        const name = this.uploader.methods.getName(id);
                        const ext = (getFileExtension(name) || '').toLowerCase();
                        const key = `${folder}/profile.${ext}`;
                        this.pendingKeyByFileId[id] = key;
                        return key;
                    },
                },
                validation: {
                    // NOTE: do not set sizeLimit here. Fine Uploader would add a
                    // `content-length-range` policy condition whose max must match
                    // the server's AWS_TEMPLATE_SIZE_LIMIT exactly, or the policy is
                    // rejected ("Invalid policy document"). The user-facing 1.5MB
                    // cap is enforced in onFileSelected instead.
                    allowedExtensions: ACCEPTED_EXTENSIONS,
                    stopOnFirstInvalidFile: true,
                },
                callbacks: {
                    onSubmitted: () => this.setState({ uploading: true, uploadProgress: 0 }),
                    onProgress: (id, name, uploaded, total) => {
                        if (total > 0) {
                            this.setState({ uploadProgress: Math.round((uploaded / total) * 100) });
                        }
                    },
                    onComplete: this.onUploadComplete,
                    onError: this.onUploadError,
                },
            },
        });

        this.setState({ uploaderReady: true });
    }

    canEdit() {
        return this.props.editable && this.props.client && this.props.client.id && this.state.uploaderReady && !!this.uploader;
    }

    openFilePicker() {
        if (this.fileInput) {
            this.fileInput.value = null;
            this.fileInput.click();
        }
    }

    onClick(evt) {
        if (evt) {
            evt.stopPropagation();
        }

        // The editor modal is a React child of this clickable container, so its
        // events bubble here through the portal. Ignore them while the editor is
        // open, otherwise interacting with (or closing) the editor would re-open
        // it and reset the selected image.
        if (this.state.editorOpen) {
            return;
        }

        if (!this.canEdit() || this.state.uploading) {
            return;
        }

        // Always open the editor; the OS file picker is only triggered from the
        // editor's "Replace / Choose photo" button, never directly from the
        // avatar click. With an existing picture the editor opens on it (with the
        // saved crop); otherwise it opens empty, prompting to choose a photo.
        const client = this.props.client || {};
        const url = client[PROFILE_PICTURE_URL_FIELD];
        const crop = (url && client[PROFILE_PICTURE_CROP_FIELD]) || {};
        this.setState({
            editorOpen: true,
            editorFile: null,
            editorImageUrl: url || null,
            editorInitScale: url ? crop.scale : undefined,
            editorInitPosition: url ? crop.position : undefined,
        });
    }

    onFileSelected(evt) {
        const file = evt && evt.target && evt.target.files && evt.target.files[0];
        if (!file) {
            return;
        }

        const extension = (getFileExtension(file.name) || '').toLowerCase();
        if (ACCEPTED_EXTENSIONS.indexOf(extension) === -1) {
            this.props.dispatch(caseSetServerError('Only JPG and PNG images are supported.'));
            return;
        }

        if (file.size > MAX_SIZE_BYTES) {
            this.props.dispatch(caseSetServerError('Profile picture must be 1.5MB or smaller.'));
            return;
        }

        // Both first-time selection and "Replace photo" land here; open (or refresh)
        // the editor with the new file and a default centered crop.
        this.setState({
            editorOpen: true,
            editorFile: file,
            editorImageUrl: null,
            editorInitScale: undefined,
            editorInitPosition: undefined,
        });
    }

    onEditorSave(data) {
        // Demo accounts cannot store files (the server refuses to sign the
        // upload); explain instead of surfacing a raw upload error.
        if (isDemoAccount(this.props.loggedIn)) {
            this.setState({ editorOpen: false, editorFile: null, editorImageUrl: null });
            this.props.dispatch(caseSetServerError(
                'Photo uploads are disabled in the live demo. Sign up for your own account to add contact photos.'));
            return;
        }

        // Only the cropped image is uploaded; the original is discarded.
        const blob = data && data.blob;
        if (!blob) {
            this.props.dispatch(caseSetServerError('Could not process the image.'));
            return;
        }

        this.setState({ uploading: true, uploadProgress: 0 });
        try {
            // Upload as a File via addFiles (the same path the working case-file
            // uploader uses) rather than addBlobs.
            let file;
            try {
                file = new File([blob], 'profile.jpg', { type: 'image/jpeg' });
            } catch (e) {
                file = blob;
                file.name = 'profile.jpg';
            }

            this.uploader.methods.addFiles([file]);
        } catch (error) {
            console.error(new Error().stack, error);
            this.setState({ uploading: false });
            this.props.dispatch(caseSetServerError('Could not start upload.'));
        }
    }

    onEditorError(message) {
        this.props.dispatch(caseSetServerError(message));
    }

    onEditorCancel() {
        if (this.state.uploading) {
            return;
        }

        this.setState({ editorOpen: false, editorFile: null, editorImageUrl: null });
    }

    onUploadComplete(id, name, response) {
        const key = this.pendingKeyByFileId[id];
        delete this.pendingKeyByFileId[id];

        if (!response || !response.success || !key) {
            console.error('Profile picture upload failed (onComplete):', { id, name, response });
            this.setState({ uploading: false });
            if (response && !response.success) {
                const detail = (response && (response.error || response.message)) || '';
                this.props.dispatch(caseSetServerError(`Profile picture upload failed.${detail ? ' ' + detail : ''}`));
            }
            return;
        }

        // Store the cropped image key and clear any legacy crop metadata so the
        // already-cropped image renders as-is.
        this.persist({ [PROFILE_PICTURE_KEY_FIELD]: key, [PROFILE_PICTURE_CROP_FIELD]: null });
    }

    onUploadError(id, name, errorReason, xhr) {
        console.error('Profile picture upload error:', { id, name, errorReason, status: xhr && xhr.status, response: xhr && xhr.responseText });
        delete this.pendingKeyByFileId[id];
        this.setState({ uploading: false });
        this.props.dispatch(caseSetServerError(errorReason || 'Profile picture upload failed.'));
    }

    async persist(fields) {
        try {
            const current = this.props.client || {};
            const updated = { ...current, ...fields };
            // The signed view URL is derived server-side; never persist it.
            if (updated[PROFILE_PICTURE_URL_FIELD]) {
                delete updated[PROFILE_PICTURE_URL_FIELD];
            }

            const result = await this.props.dispatch(contactFormSaveAction(updated));
            const savedClient = result && result.data && result.data.client;
            if (savedClient) {
                this.props.dispatch(clientEditAction(savedClient));
                if (this.props.currentCase &&
                    this.props.currentCase.client &&
                    this.props.currentCase.client.id === savedClient.id) {
                    this.props.dispatch(caseClientSetAction(savedClient));
                }
            }
        } catch (error) {
            console.error(new Error().stack, error);
            this.props.dispatch(caseSetServerError(error));
        }

        this.setState({ uploading: false, editorOpen: false, editorFile: null, editorImageUrl: null });
    }

    async onRemove() {
        if (!this.canEdit() || this.state.uploading) {
            return;
        }

        this.setState({ uploading: true });
        try {
            const id = this.props.client.id;
            const result = await request.delete(`/api/v1/client/${id}/profile_picture`, getClientAuthHeader());
            const savedClient = result && result.data && result.data.client;
            if (savedClient) {
                this.props.dispatch(clientEditAction(savedClient));
                if (this.props.currentCase &&
                    this.props.currentCase.client &&
                    this.props.currentCase.client.id === savedClient.id) {
                    this.props.dispatch(caseClientSetAction(savedClient));
                }
            }
        } catch (error) {
            console.error(new Error().stack, error);
            this.props.dispatch(caseSetServerError('Failed to remove profile picture.'));
        }

        this.setState({ uploading: false, editorOpen: false, editorFile: null, editorImageUrl: null });
    }

    handleHoverIn() {
        if (this.canEdit()) {
            this.setState({ hover: true });
        }
    }

    handleHoverOut() {
        this.setState({ hover: false });
    }

    render() {
        const { client = {}, size = 70, mobile, deleted } = this.props;
        const url = client[PROFILE_PICTURE_URL_FIELD];
        const crop = client[PROFILE_PICTURE_CROP_FIELD];
        const editable = this.canEdit();
        const dimensions = mobile ? Math.min(size, 80) : size;
        const fontSize = mobile ? '36px' : '52px';

        // The container is the circle itself: it clips its contents (image and the
        // overlay) so everything stays aligned to the avatar regardless of size.
        const containerStyle = {
            position: 'relative',
            display: 'inline-block',
            width: dimensions,
            height: dimensions,
            borderRadius: '50%',
            overflow: 'hidden',
            backgroundColor: deleted ? '#000' : FALLBACK_COLOR,
            cursor: editable ? 'pointer' : 'default',
            verticalAlign: 'middle',
            lineHeight: 0,
        };

        const validRect = crop && crop.rect &&
            crop.rect.width > 0 && crop.rect.height > 0;

        let avatarBody;
        if (url && validRect) {
            // Reproduce the editor's circular crop exactly from the normalised rect.
            const rect = crop.rect;
            const bgW = dimensions / rect.width;
            const bgH = dimensions / rect.height;
            const croppedStyle = {
                width: '100%',
                height: '100%',
                backgroundImage: `url("${url}")`,
                backgroundRepeat: 'no-repeat',
                backgroundSize: `${bgW}px ${bgH}px`,
                backgroundPosition: `${-rect.x * bgW}px ${-rect.y * bgH}px`,
                opacity: deleted ? 0.5 : 1,
                display: 'block',
            };
            avatarBody = (<div style={croppedStyle} />);
        } else if (url) {
            // Legacy picture saved before cropping existed.
            const imgStyle = {
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
                opacity: deleted ? 0.5 : 1,
            };
            avatarBody = (<img src={url} alt="Contact" style={imgStyle} />);
        } else {
            const glyphStyle = {
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: deleted ? 0.5 : 1,
            };
            avatarBody = (<div style={glyphStyle}><Glyphicon glyph="user" style={{ fontSize, color: '#7a8a99' }} /></div>);
        }

        // Prompt to add a photo when the avatar is editable and empty. Large
        // avatars (e.g. the case header) show the prompt permanently; small ones
        // (e.g. the client table) only reveal a compact camera hint on hover so
        // the table isn't cluttered. Once a picture exists there is no overlay.
        //
        const isLarge = dimensions >= 70;
        const showAddOverlay = editable && !url && (isLarge || this.state.hover);
        const overlayHeight = Math.max(16, Math.floor(dimensions * 0.32));
        const overlayStyle = {
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: overlayHeight,
            lineHeight: `${overlayHeight}px`,
            backgroundColor: 'rgba(0,0,0,0.55)',
            color: '#fff',
            fontSize: Math.max(9, Math.floor(dimensions * 0.12)),
            textAlign: 'center',
            pointerEvents: 'none',
        };

        return (
            <div
                style={containerStyle}
                onClick={editable ? this.onClick : undefined}
                onMouseEnter={this.handleHoverIn}
                onMouseLeave={this.handleHoverOut}
                title={showAddOverlay ? 'Add photo' : undefined}
            >
                {avatarBody}
                {showAddOverlay && (
                    <div style={overlayStyle}>{isLarge ? <span><Glyphicon glyph="camera" /> Add</span> : <Glyphicon glyph="camera" />}</div>
                )}
                {editable && (
                    <input
                        type="file"
                        accept={ACCEPT_ATTR}
                        ref={ref => { this.fileInput = ref; }}
                        onChange={this.onFileSelected}
                        style={{ display: 'none' }}
                    />
                )}
                {this.state.editorOpen && (
                    <ProfilePictureEditor
                        open={this.state.editorOpen}
                        imageFile={this.state.editorFile}
                        imageUrl={this.state.editorImageUrl}
                        initialScale={this.state.editorInitScale}
                        initialPosition={this.state.editorInitPosition}
                        hasExisting={!!url}
                        uploading={this.state.uploading}
                        progress={this.state.uploadProgress}
                        onSave={this.onEditorSave}
                        onReplace={this.openFilePicker}
                        onDelete={this.onRemove}
                        onCancel={this.onEditorCancel}
                        onError={this.onEditorError}
                    />
                )}
            </div>
        );
    }
}

ClientAvatar.propTypes = {
    client: PropTypes.object,
    size: PropTypes.number,
    editable: PropTypes.bool,
    mobile: PropTypes.bool,
    deleted: PropTypes.bool,
};

const mapStateToProps = (state) => {
    return {
        loggedIn: state.user.loggedIn,
        currentCase: state.cases && state.cases.current,
    };
};

export default connect(mapStateToProps)(ClientAvatar);
